import { notifications } from '@mantine/notifications';
import { IconCheck, IconDeviceMobile, IconExclamationCircle, IconMail } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { User } from '@xver/@types/user';
import { Endpoints } from '@xver/apis';
import { Button, InputField } from '@xver/components/Atoms';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { OtpInputField } from '@xver/components/Molecules';
import useLogout, { PASSWORD_CHANGED_NOTICE } from '@xver/hooks/useLogout';
import { getErrorMessage } from '@xver/utils/error';
import storage from '@xver/utils/storage';

interface FormErrors {
    oldPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
    otp?: string;
}

interface ChangePasswordSectionProps {
    user: User;
}

function maskEmail(value: string) {
    const [local, domain] = value.split('@');
    if (!local || !domain) return value;
    if (local.length <= 2) return `${local[0]}*@${domain}`;
    return `${local[0]}${'*'.repeat(Math.max(local.length - 2, 1))}${local.at(-1)}@${domain}`;
}

function maskPhone(value: string) {
    if (value.length < 4) return value;
    return `${value.slice(0, 2)}${'*'.repeat(value.length - 4)}${value.slice(-2)}`;
}

export default function ChangePasswordSection({ user }: ChangePasswordSectionProps) {
    const { t } = useTranslation('security');
    const logout = useLogout();

    const [method, setMethod] = useState<'email' | 'phone'>('email');
    const [stage, setStage] = useState<'idle' | 'enter-otp'>('idle');
    const [otp, setOtp] = useState('');
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [otpResetKey, setOtpResetKey] = useState(0);

    const requestMutation = useMutation({
        mutationFn: () => {
            const token = storage.get<string>('_zugang', '');
            if (!token) throw new Error(t('errors.missingToken'));
            return Endpoints.v1.Authentication.requestChangePassword(token, {
                change_type: method,
            });
        },
        onSuccess: () => {
            notifications.show({
                title: t('toast.codeSentTitle'),
                message: t('toast.codeSentMessage'),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            setStage('enter-otp');
        },
        onError: (err: unknown) => {
            notifications.show({
                title: t('toast.errorTitle'),
                message: getErrorMessage(err) ?? t('toast.errorMessage'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const validation = useMemo<FormErrors>(() => {
        const errors: FormErrors = {};
        if (!oldPassword) errors.oldPassword = t('errors.passwordRequired');
        if (!newPassword) errors.newPassword = t('errors.passwordRequired');
        else if (newPassword.length < 6) errors.newPassword = t('errors.passwordLength');
        if (newPassword !== confirmPassword) errors.confirmPassword = t('errors.passwordMismatch');
        if (otp.length !== 6) errors.otp = t('errors.otpInvalid');
        return errors;
    }, [oldPassword, newPassword, confirmPassword, otp, t]);

    const changeMutation = useMutation({
        mutationFn: () =>
            Endpoints.v1.Authentication.changePassword(otp, {
                old_password: oldPassword,
                new_password: newPassword,
                confirm_password: confirmPassword,
            }),
        onSuccess: () => {
            sessionStorage.setItem(PASSWORD_CHANGED_NOTICE, String(performance.timeOrigin));
            logout();
        },
        onError: (err: unknown) => {
            notifications.show({
                title: t('toast.errorTitle'),
                message: getErrorMessage(err) ?? t('toast.errorMessage'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const handleSubmit = () => {
        if (!submitAttempted) setSubmitAttempted(true);
        if (Object.keys(validation).length > 0) return;
        changeMutation.mutate();
    };

    const handleResetStage = () => {
        setStage('idle');
        setOtp('');
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setSubmitAttempted(false);
        setOtpResetKey((k) => k + 1);
    };

    return (
        <DialogBox
            label={t('password.title')}
            description={t('password.description')}
            classNames="p-4 gap-4"
        >
            {stage === 'idle' ? (
                <div className="flex flex-col gap-4">
                    <span className="text-sm text-fg/70">{t('password.deliveryHeading')}</span>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(
                            [
                                {
                                    id: 'email' as const,
                                    icon: IconMail,
                                    label: t('password.methodEmail'),
                                    value: maskEmail(user.email),
                                },
                                {
                                    id: 'phone' as const,
                                    icon: IconDeviceMobile,
                                    label: t('password.methodPhone'),
                                    value: maskPhone(user.phoneNumber),
                                },
                            ] as const
                        ).map(({ id, icon: Icon, label, value }) => {
                            const selected = method === id;
                            return (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={() => setMethod(id)}
                                    className={[
                                        'relative flex flex-col gap-2 rounded-lg border p-4 text-left transition',
                                        selected
                                            ? 'border-blue-500 ring-2 ring-blue-500/30 bg-fg/2'
                                            : 'border-fg/15 hover:border-fg/30',
                                    ].join(' ')}
                                >
                                    <Icon size={20} stroke={1.5} />
                                    <span className="text-sm font-medium">{label}</span>
                                    <span className="text-xs text-fg/68">{value}</span>
                                    <span
                                        className={[
                                            'absolute top-3 right-3 h-3 w-3 rounded-full border',
                                            selected
                                                ? 'border-blue-500 bg-blue-500'
                                                : 'border-fg/40',
                                        ].join(' ')}
                                        aria-hidden="true"
                                    />
                                </button>
                            );
                        })}
                    </div>
                    <div className="flex justify-start">
                        <Button
                            onClick={() => requestMutation.mutate()}
                            isLoading={requestMutation.isPending}
                        >
                            {t('password.requestCode')}
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="w-full h-auto flex justify-center items-center">
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col justify-start">
                            <span className="text-sm mb-2">{t('password.otp')}</span>
                            <OtpInputField
                                length={6}
                                onComplete={setOtp}
                                onChange={setOtp}
                                resetKey={otpResetKey}
                            />
                            {submitAttempted && validation.otp && (
                                <p className="text-xs text-red-400 light:text-red-700 mt-2">
                                    {validation.otp}
                                </p>
                            )}
                        </div>
                        <InputField
                            size="md"
                            type="password"
                            passwordReveal
                            label={t('password.oldPassword')}
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                            error={submitAttempted ? validation.oldPassword : undefined}
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <InputField
                                size="md"
                                type="password"
                                passwordReveal
                                label={t('password.newPassword')}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                error={submitAttempted ? validation.newPassword : undefined}
                            />
                            <InputField
                                size="md"
                                type="password"
                                passwordReveal
                                label={t('password.confirmPassword')}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                error={submitAttempted ? validation.confirmPassword : undefined}
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="secondary" onClick={handleResetStage}>
                                {t('password.cancel')}
                            </Button>
                            <Button onClick={handleSubmit} isLoading={changeMutation.isPending}>
                                {t('password.submit')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </DialogBox>
    );
}
